import { Form, Formik } from "formik";
import { useMemo, useRef, useState } from "react";
import { sanitizeValues } from "../../../models/utils";
import { handleAPIError } from "../../../utils/index.jsx";
import Tabs from "../../UI/Tabs";
import TabbedForm from "../../UI/TabbedForm";
import ButtonCard from "../../UI/ButtonCard";
import IconAndText from "../../UI/IconAndText";
import {
  MANDAT_PRELEVEMENT_IV,
  mandatPrelevementSchema,
} from "../../../models/mandatPrelevement";
import TextInput from "../../UI/TextInput";
import FormikSubmitButton from "../../UI/FormikSubmitButton";
import useMandatPrelevement from "../../../hooks/use-mandat-prelevement";
import Callout from "../../UI/Callout";
import Radio from "../../UI/Radio";
import RadioGroup from "../../UI/RadioGroup";
import Tooltip from "../../UI/Tooltip";
import { Interrogation } from "../../UI/Icons";

export default function MandatForm({
  tenantId,
  property,
  modal: ModalElement,
  onSubmit,
}) {
  const [error, setError] = useState("");
  const [type, setType] = useState("");
  const [selectedMandatId, setSelectedMandatId] = useState(null);
  const {
    createMandatPrelevement,
    updateMandatPrelevement,
    mandatPrelevements,
  } = useMandatPrelevement();

  const formRef = useRef();
  const tenantMandats = useMemo(
    () => mandatPrelevements?.filter((m) => m.id_tenant === tenantId) || [],
    [mandatPrelevements, tenantId],
  );

  const selectedMandat = useMemo(
    () =>
      selectedMandatId
        ? tenantMandats.find((m) => m.id === selectedMandatId)
        : null,
    [selectedMandatId, tenantMandats],
  );

  const initialValues = useMemo(
    () => ({
      ...MANDAT_PRELEVEMENT_IV,
      id_tenant: tenantId,
      id_profile_bailleur: property?.id_profile_nom_owner,
      ...(selectedMandat && {
        iban: selectedMandat.iban || "",
        bic: selectedMandat.bic || "",
        rum: selectedMandat.rum || "",
        date_mandat: selectedMandat.date_mandat || "",
        instrument: selectedMandat.instrument || "core",
      }),
    }),
    [tenantId, property?.id_profile_nom_owner, selectedMandat],
  );

  async function submitHandler(values, { setSubmitting }) {
    const sanitizedValues = { ...sanitizeValues(values) };
    try {
      if (selectedMandatId) {
        // Lors de la modification, le backend n'accepte pas les champs
        // `id_tenant` et `id_profile_bailleur` dans le payload (Unknown field).
        // On les supprime donc pour ne mettre à jour que les champs éditables.
        const {
          id_tenant: _idTenant,
          id_profile_bailleur: _idProfileBailleur,
          ...updatableFields
        } = sanitizedValues;
        await updateMandatPrelevement(selectedMandatId, updatableFields);
      } else {
        await createMandatPrelevement(sanitizedValues);
      }
      onSubmit();
      setError("");
      setSelectedMandatId(null);
    } catch (error) {
      setError(handleAPIError(error));
    }
    setSubmitting(false);
  }

  return (
    <>
      <Formik
        initialValues={initialValues}
        enableReinitialize={true}
        onSubmit={submitHandler}
        validationSchema={mandatPrelevementSchema}
      >
        {(formikProps) => {
          let tabIndex = 0;
          return (
            <>
              <TabbedForm id="mandat-form" ref={formRef}>
                <ModalElement
                  confirmCloseText={
                    "Votre formulaire n'a pas été sauvegardé. Voulez-vous vraiment quitter ?"
                  }
                  shouldConfirm={() => formikProps.dirty}
                  size={"big"}
                  stickyHeader
                  blueHeader
                  onClose={() => {
                    formRef.current?.resetTab();
                  }}
                  header={
                    <TabbedForm.TabList>
                      <TabbedForm.Tab panelId="type-mandat" index={tabIndex++}>
                        Type d'action
                      </TabbedForm.Tab>
                      {type && (
                        <>
                          {type === "ajouter" && (
                            <TabbedForm.Tab
                              panelId="ajouter"
                              index={tabIndex++}
                            >
                              Ajouter un mandat
                            </TabbedForm.Tab>
                          )}
                          {type === "modifier" && (
                            <>
                              <TabbedForm.Tab
                                panelId="modifier-select"
                                index={tabIndex++}
                              >
                                Sélectionner un mandat
                              </TabbedForm.Tab>
                              {selectedMandatId && (
                                <TabbedForm.Tab
                                  panelId="modifier"
                                  index={tabIndex++}
                                >
                                  Modifier le mandat
                                </TabbedForm.Tab>
                              )}
                            </>
                          )}
                          {type === "creer" && (
                            <TabbedForm.Tab panelId="creer" index={tabIndex++}>
                              Générer un mandat{" "}
                            </TabbedForm.Tab>
                          )}
                        </>
                      )}
                    </TabbedForm.TabList>
                  }
                >
                  <InnerForm
                    error={error}
                    setType={setType}
                    setSelectedMandatId={setSelectedMandatId}
                    tenantMandats={tenantMandats}
                  />
                </ModalElement>
              </TabbedForm>
            </>
          );
        }}
      </Formik>
    </>
  );
}

function MandatFormFields({ error, submitLabel }) {
  return (
    <>
      <TextInput
        label="IBAN du locataire"
        name="iban"
        transformValue={(v) => v.toUpperCase()}
      />
      <TextInput
        label="BIC du locataire"
        name="bic"
        transformValue={(v) => v.toUpperCase()}
      />
      <TextInput label="ID (RUM) du mandat" name="rum" />
      <TextInput label="Date du mandat" name="date_mandat" type={"date"} />
      <RadioGroup className={"marginT10 flex wrap gap10"}>
        <Radio
          name="instrument"
          value="core"
          label={
            <div className={"flex"}>
              <p className={"marginR5"}>CORE</p>
              <Tooltip content={"Entreprise en particulier (défaut)"}>
                <Interrogation />
              </Tooltip>
            </div>
          }
        />
        <Radio
          name="instrument"
          value="b2b"
          label={
            <div className={"flex"}>
              <p className={"marginR5"}>B2B</p>
              <Tooltip content={"Réservé entreprise. Mandat spécial"}>
                <Interrogation />
              </Tooltip>
            </div>
          }
        />
      </RadioGroup>

      <div className={"text-right"}>
        <FormikSubmitButton requiresWrite>{submitLabel}</FormikSubmitButton>
      </div>
      <Callout variant="danger">{error}</Callout>
    </>
  );
}

function InnerForm({ error, setType, setSelectedMandatId, tenantMandats }) {
  const { selectTab } = Tabs.useActiveTab();

  function next(type) {
    let nextTab = "";
    switch (type) {
      case "ajouter":
        nextTab = "ajouter";
        break;
      case "modifier":
        nextTab = "modifier-select";
        break;
      default:
        nextTab = "creer";
    }
    selectTab(nextTab, true);
    setType(type);
  }

  function selectMandat(mandatId) {
    setSelectedMandatId(mandatId);
    selectTab("modifier", true);
  }
  return (
    <Form>
      <div className="marginT10">
        <TabbedForm.TabPanel id="type-mandat">
          {/*
                          <ButtonCard
                            className={
                              "flex space-between align-items-center w-100 marginT10"
                            }
                            onClick={() => {
                              next("creer");
                            }}
                          >
                            <IconAndText
                              icon={
                                <img
                                  src={`/ChoseCreerMandat.png`}
                                  alt="Générer un mandat de prélèvement"
                                  className={"center"}
                                />
                              }
                              text={"Générer un mandat de prélèvement"}
                            />
                            <ArrowRight classname={"marginR10"} />
                          </ButtonCard>
                        */}
          <h2 className={"text-center"}>Prélèvement de loyer</h2>
          <p className={"primaryText marginT10"}>
            Le prélèvement est fait par vous, pas par Qalimo.
          </p>
          <p className={"marginT10"}>
            Vous devez obtenir auprès de votre banque un numéro ICS.
          </p>
          <p className={"marginT10 primaryText"}>Vous devrez tous les mois :</p>
          <ul>
            <li>générer le fichier de prélèvement</li>
            <li>le transmettre à votre banque</li>
            <li>la banque procède aux prélèvements</li>
          </ul>

          <ButtonCard
            className={"flex space-between align-items-center w-100 marginT10"}
            onClick={() => {
              next("ajouter");
            }}
            requiresWrite
          >
            <IconAndText
              icon={
                <img
                  src={`/ChoseAjoutMandat.png`}
                  alt="Ajouter un mandat de prélèvement existant"
                  className={"center"}
                />
              }
              text={"Ajouter un mandat de prélèvement déjà existant"}
            />
          </ButtonCard>

          {tenantMandats.length > 0 && (
            <ButtonCard
              className={
                "flex space-between align-items-center w-100 marginT10"
              }
              onClick={() => {
                next("modifier");
              }}
              requiresWrite
            >
              <IconAndText
                icon={
                  <img
                    src={`/ChoseAjoutMandat.png`}
                    alt="Modifier un mandat de prélèvement existant"
                    className={"center"}
                  />
                }
                text={"Modifier un mandat de prélèvement existant"}
              />
            </ButtonCard>
          )}
        </TabbedForm.TabPanel>
        <TabbedForm.TabPanel id="modifier-select">
          {tenantMandats.length === 0 ? (
            <Callout variant="info">
              Aucun mandat de prélèvement disponible pour ce locataire.
            </Callout>
          ) : (
            <>
              <h2 className={"text-center"}>
                Sélectionner un mandat à modifier
              </h2>
              {tenantMandats.map((mandat) => (
                <ButtonCard
                  key={mandat.id}
                  className={
                    "flex space-between align-items-center w-100 marginT10"
                  }
                  onClick={() => selectMandat(mandat.id)}
                >
                  <div>
                    <p className={"marginB5"}>IBAN: {mandat.iban}</p>
                    <p className={"smallText"}>RUM: {mandat.rum}</p>
                  </div>
                </ButtonCard>
              ))}
            </>
          )}
        </TabbedForm.TabPanel>
        <TabbedForm.TabPanel id="modifier">
          <MandatFormFields error={error} submitLabel="Modifier" />
        </TabbedForm.TabPanel>
        <TabbedForm.TabPanel id="ajouter">
          <MandatFormFields error={error} submitLabel="Valider" />
        </TabbedForm.TabPanel>
        <TabbedForm.TabPanel id="creer"></TabbedForm.TabPanel>
      </div>
    </Form>
  );
}
